/**
 * google_books_search - AI Tool for Searching Google Books API
 * Search for books, retrieve metadata, check availability
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('GOOGLE_BOOKS_SEARCH', { enabled: true });

export const google_books_search: SimpleDocCommand = {
  id: 'google_books_search',
  logicalName: 'google_books_search',
  version: 'v1',
  
  description: `Search Google Books API for book information, metadata, and availability.

**USE WHEN:** User asks about books, authors, ISBNs, publication info, or book availability.

**QUERY SYNTAX:**

General search:
{ "query": "machine learning textbooks" }

Search by ISBN (most precise):
{ "query": "isbn:9780134685991" }

Search by title:
{ "query": "intitle:Python Crash Course" }

Search by author:
{ "query": "inauthor:McKinney" }

Combined search:
{ "query": "intitle:Learning inauthor:Ng subject:AI" }

Filter to free ebooks only:
{ "query": "python programming", "filter": "free-ebooks" }

**SPECIAL KEYWORDS:**
- intitle: Search in title
- inauthor: Search in author name
- inpublisher: Search in publisher name
- subject: Search by category/subject
- isbn: Search by ISBN (10 or 13 digit)
- lccn: Search by Library of Congress Control Number
- oclc: Search by Online Computer Library Center number

**FILTERS:**
- ebooks: Only ebooks
- free-ebooks: Only free ebooks
- full: Only books with full text available
- paid-ebooks: Only paid ebooks
- partial: Books with preview available

**SORTING:**
- relevance: Most relevant first (default)
- newest: Most recently published first

**LIMITS:** Max 40 results per query (default 10).

**RETURNS:** Book ID, title, authors, ISBNs, publisher, publication date, description, page count, categories, language, preview/info links, thumbnail, availability (ebook, free, PDF, EPUB).`,

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query. Use special keywords: intitle:, inauthor:, inpublisher:, subject:, isbn:',
        required: true,
        examples: [
          'machine learning',
          'isbn:9780134685991',
          'intitle:Python inauthor:McKinney',
          'subject:fiction',
          'inauthor:Tolkien'
        ]
      },
      {
        name: 'search_type',
        type: 'string',
        description: 'Type of search to perform',
        required: false,
        examples: ['general', 'by_isbn', 'by_title', 'by_author'],
        validator: (value: string) => {
          if (!value) return null;
          const validTypes = ['general', 'by_isbn', 'by_title', 'by_author'];
          if (!validTypes.includes(value)) {
            return `search_type must be one of: ${validTypes.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'max_results',
        type: 'number',
        description: 'Number of results to return (1-40, default 10)',
        required: false,
        validator: (value: number) => {
          if (value && (value < 1 || value > 40)) {
            return 'max_results must be between 1 and 40';
          }
          return null;
        }
      },
      {
        name: 'filter',
        type: 'string',
        description: 'Filter results by availability',
        required: false,
        examples: ['ebooks', 'free-ebooks', 'full', 'paid-ebooks', 'partial'],
        validator: (value: string) => {
          if (!value) return null;
          const validFilters = ['ebooks', 'free-ebooks', 'full', 'paid-ebooks', 'partial'];
          if (!validFilters.includes(value)) {
            return `filter must be one of: ${validFilters.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'order_by',
        type: 'string',
        description: 'Sort order for results',
        required: false,
        examples: ['relevance', 'newest'],
        validator: (value: string) => {
          if (!value) return null;
          const validOrders = ['relevance', 'newest'];
          if (!validOrders.includes(value)) {
            return `order_by must be one of: ${validOrders.join(', ')}`;
          }
          return null;
        }
      }
    ]
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        
        return {
          type: 'google_books_results',
          summary: result.message || `Found ${data.totalItems || 0} books`,
          data: {
            books: data.books || [],
            count: data.totalItems || 0,
            query: toolInput?.query,
            filters: {
              filter: toolInput?.filter,
              order_by: toolInput?.order_by,
              max_results: toolInput?.max_results || 10
            }
          },
          meta: {
            searchParams: {
              query: toolInput?.query,
              search_type: toolInput?.search_type,
              filter: toolInput?.filter,
              order_by: toolInput?.order_by
            }
          }
        };
      }
      
      return {
        type: 'google_books_results',
        summary: result?.message || 'Search completed',
        data: { books: [], count: 0 }
      };
    } catch (error) {
      console.warn('[google_books_search] Error formatting view data:', error);
      return {
        type: 'google_books_results',
        summary: 'Search completed',
        data: { books: [], count: 0 }
      };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      log.log('[google_books_search] Searching Google Books:', params);
      
      // Validate query is not empty
      if (!params.query || params.query.trim() === '') {
        return createErrorResult(
          'Empty search query',
          'Search query cannot be empty. Please provide a search term.',
          'validation',
          {
            data: { query: params.query },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
      
      // Build query with search_type shortcuts
      let searchQuery = params.query;
      if (params.search_type === 'by_isbn' && !searchQuery.startsWith('isbn:')) {
        searchQuery = `isbn:${searchQuery}`;
      } else if (params.search_type === 'by_title' && !searchQuery.startsWith('intitle:')) {
        searchQuery = `intitle:${searchQuery}`;
      } else if (params.search_type === 'by_author' && !searchQuery.startsWith('inauthor:')) {
        searchQuery = `inauthor:${searchQuery}`;
      }
      
      log.log('[google_books_search] Final query:', searchQuery);
      
      // Call Google Books API through backend
      const { googleBooksAdapter } = await import('../../services/integrations/adapters/GoogleBooksAdapter');
      
      const searchParams: any = {
        q: searchQuery,
        maxResults: params.max_results || 10
      };
      
      if (params.filter) searchParams.filter = params.filter;
      if (params.order_by) searchParams.orderBy = params.order_by;
      
      const searchResult = await googleBooksAdapter.searchBooks(searchParams);
      
      log.log('[google_books_search] Search complete:', searchResult.totalItems, 'books');
      
      // Format results for display
      const formattedBooks = (searchResult.items || []).map((item: any) => {
        const volumeInfo = item.volumeInfo || {};
        const saleInfo = item.saleInfo || {};
        const accessInfo = item.accessInfo || {};
        
        // Extract ISBNs
        const identifiers = volumeInfo.industryIdentifiers || [];
        const isbn10 = identifiers.find((id: any) => id.type === 'ISBN_10')?.identifier;
        const isbn13 = identifiers.find((id: any) => id.type === 'ISBN_13')?.identifier;
        
        // Extract preview/info links
        const links = volumeInfo.imageLinks || {};
        
        return {
          id: item.id,
          title: volumeInfo.title || 'Unknown Title',
          subtitle: volumeInfo.subtitle || null,
          authors: volumeInfo.authors || [],
          publisher: volumeInfo.publisher || null,
          publishedDate: volumeInfo.publishedDate || null,
          description: volumeInfo.description || null,
          pageCount: volumeInfo.pageCount || null,
          categories: volumeInfo.categories || [],
          language: volumeInfo.language || null,
          isbn10: isbn10 || null,
          isbn13: isbn13 || null,
          previewLink: volumeInfo.previewLink || null,
          infoLink: volumeInfo.infoLink || null,
          thumbnail: links.thumbnail || links.smallThumbnail || null,
          averageRating: volumeInfo.averageRating || null,
          ratingsCount: volumeInfo.ratingsCount || null,
          availability: {
            isEbook: saleInfo.isEbook || false,
            saleability: saleInfo.saleability || 'NOT_FOR_SALE',
            listPrice: saleInfo.listPrice || null,
            pdfAvailable: accessInfo.pdf?.isAvailable || false,
            epubAvailable: accessInfo.epub?.isAvailable || false,
            webReaderLink: accessInfo.webReaderLink || null
          }
        };
      });
      
      const message = searchResult.totalItems === 0
        ? `No books found matching "${searchQuery}"`
        : `Found ${searchResult.totalItems} book${searchResult.totalItems === 1 ? '' : 's'} matching "${searchQuery}"`;
      
      return createSuccessResult(
        message,
        {
          books: formattedBooks,
          totalItems: searchResult.totalItems || 0,
          query: searchQuery,
          originalQuery: params.query,
          filters: searchParams
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime
          },
          ui: {
            resultPreview: 'summary',
            refreshContext: false
          }
        }
      );
      
    } catch (error: any) {
      log.error('[google_books_search] Search failed:', error);
      
      return createErrorResult(
        'Google Books search failed',
        error.message || 'Unknown error occurred while searching Google Books',
        'execution',
        {
          data: {
            query: params.query,
            errorDetails: error.details || error.message
          },
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    }
  },

  // Tool metadata
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  examples: {
    good: [
      {
        input: { query: 'isbn:9780134685991' },
        expectedOutput: createSuccessResult('Found 1 book', {
          books: [
            { 
              title: 'Python for Data Analysis',
              authors: ['Wes McKinney'],
              isbn13: '9780134685991'
            }
          ]
        }),
        description: 'Search by ISBN (most precise)',
        context: 'When user knows the exact ISBN'
      },
      {
        input: { 
          query: 'intitle:Python inauthor:McKinney',
          max_results: 5
        },
        expectedOutput: createSuccessResult('Found 3 books', {
          books: []
        }),
        description: 'Combined title and author search',
        context: 'Finding books by specific author with keyword in title'
      },
      {
        input: {
          query: 'machine learning',
          filter: 'free-ebooks',
          order_by: 'newest'
        },
        expectedOutput: createSuccessResult('Found 15 books', {
          books: []
        }),
        description: 'Find recent free ebooks on topic',
        context: 'User wants freely available resources'
      },
      {
        input: {
          query: 'subject:fiction inauthor:Tolkien',
          max_results: 20
        },
        expectedOutput: createSuccessResult('Found 12 books', {
          books: []
        }),
        description: 'Search by subject and author',
        context: 'Finding all fiction works by specific author'
      }
    ],
    bad: [
      {
        input: { query: '' },
        error: 'query is required',
        why: 'Empty search query',
        fix: 'Provide a non-empty search query'
      },
      {
        input: { query: 'test', max_results: 100 },
        error: 'max_results must be between 1 and 40',
        why: 'max_results exceeds API limit',
        fix: 'Use max_results between 1 and 40'
      },
      {
        input: { query: 'test', filter: 'invalid_filter' },
        error: 'filter must be one of: ebooks, free-ebooks, full, paid-ebooks, partial',
        why: 'Invalid filter value',
        fix: 'Use a valid filter value'
      }
    ]
  },

  performance: {
    expectedDuration: 800, // Google Books API typically responds in 300-1500ms
    complexity: 'low'
  }
};
